import { ReactNode } from 'react';
import clsx from 'clsx';

import { Autocomplete, Icon } from '@atom/mui';
import { CloseIcon, GpsFixedIcon, LocationOnIcon } from '@atom/mui/icons';
import fonts from '@atom/styles/fonts';

import { getOptionText } from './toolbarUtilities';
import { MapSearchState } from './useMapSearch';

import classes from './toolbar.module.css';

type SearchAutocompleteProps = {
  search: MapSearchState;
  anchorEl: HTMLDivElement | null;
  startAdornment: ReactNode;
};

const styles = {
  autocomplete: {
    flex: '1 1 auto',
    minWidth: 0,
    '& .MuiInputBase-input': { fontSize: fonts.lg },
  },
};

const SearchAutocomplete = ({
  search,
  anchorEl,
  startAdornment,
}: SearchAutocompleteProps) => {
  const {
    term,
    suggestions,
    handleInputChange,
    handleSelect,
    handleClear,
    handleCurrentLocation,
  } = search;

  return (
    <Autocomplete
      sx={styles.autocomplete}
      slotProps={{
        popper: {
          anchorEl,
          placement: 'bottom-start',
          style: { width: anchorEl?.clientWidth },
        },
      }}
      options={suggestions}
      inputValue={term}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      disableClearable
      disableUnderline
      filterOptions={options => options}
      getOptionLabel={getOptionText}
      renderOption={(optionProps, option) => {
        const { key, className, ...rest } = optionProps as {
          key: string;
          className?: string;
        };

        return (
          <li
            key={key}
            {...rest}
            className={clsx(className, classes.searchOption)}
          >
            <Icon size="sm" icon={LocationOnIcon} />
            <span>{getOptionText(option)}</span>
          </li>
        );
      }}
      placeholder="Search Maps"
      startAdornment={startAdornment}
      endAdornment={
        term ? (
          <Icon
            className={classes.searchIcon}
            onClick={handleClear}
            icon={CloseIcon}
          />
        ) : (
          <Icon
            className={classes.searchIcon}
            onClick={handleCurrentLocation}
            icon={GpsFixedIcon}
          />
        )
      }
      TextFieldProps={{ variant: 'standard', fullWidth: true }}
    />
  );
};

export default SearchAutocomplete;
